/**
 * Generates an isosurface mesh from filtered MWD borehole data using
 * anisotropic Gaussian RBF interpolation with data-driven plane fitting.
 */
import * as THREE from "three";
import type { CenterlineData } from "./useTunnelCenterline";
import type { SalvoJsonData, MwdRawScanData, MwdSensorName } from "./Boreholes3D";
import { fitPlaneNormal, evaluateOnGrid, marchingCubes, type RBFSample, type ScalarGrid } from "./rbfInterpolant";

const FRAME_SAMPLES = 1000;

export interface RBFGenerateOptions {
  data: CenterlineData;
  salvoData: SalvoJsonData;
  mwdRawData: Map<number, MwdRawScanData>;
  sensor: MwdSensorName;
  /** The clamped range from the color ramp filter — samples inside this range become "1" */
  clampRange?: { min: number; max: number };
  /** For categorical data: set of category values that should be treated as "inside" */
  selectedCategories?: Set<string>;
  /** Grid resolution per axis. Default 25 */
  resolution?: number;
  /** Gaussian sharpness (higher = narrower influence). Default 2 */
  power?: number;
  /** RBF range/control radius in meters. Default 6 */
  radius?: number;
  /** Flatness: how slab-like the surface is. 1 = sphere, 5 = very flat. Default 5 */
  flatness?: number;
  /** Iso-value for surface extraction (0–1). Default 0.5 */
  isoValue?: number;
  /** User-supplied plane normal (skips auto-detection via PCA). */
  planeNormal?: [number, number, number];
  /** Progress callback */
  onProgress?: (stage: string) => void;
}

export interface RBFGenerateResult {
  geometry: THREE.BufferGeometry;
  grid: ScalarGrid;
  triangleCount: number;
  sampleCount: number;
  planeNormal: [number, number, number];
  timeMs: number;
}

/**
 * Extract a new isosurface from a previously computed grid.
 * This is fast — no recomputation needed.
 */
export function extractSurfaceFromGrid(grid: ScalarGrid, isoValue: number): THREE.BufferGeometry | null {
  const verts = marchingCubes(grid, isoValue);
  if (verts.length === 0) return null;
  const geo = new THREE.BufferGeometry();
  geo.setAttribute("position", new THREE.BufferAttribute(verts, 3));
  geo.computeVertexNormals();
  return geo;
}

/**
 * Generate an isosurface from the currently filtered MWD data.
 *
 * 1. Classify each sample as inside (1) or outside (0) the color ramp range
 * 2. Fit a best-fit plane through "inside" samples via PCA
 * 3. Use anisotropic Gaussian RBF: influence is wide along the plane, thin perpendicular to it
 * 4. Extract isosurface at the given iso-value
 */
export function generateRBFVolume(opts: RBFGenerateOptions): RBFGenerateResult | null {
  const {
    data, salvoData, mwdRawData, sensor, clampRange, selectedCategories,
    resolution = 25,
    power = 2,
    radius = 6,
    flatness = 5,
    isoValue = 0.5,
    planeNormal: userPlaneNormal,
    onProgress,
  } = opts;

  const isCategorical = !!selectedCategories;
  if (!isCategorical && !clampRange) {
    console.warn("[RBF] Neither clampRange nor selectedCategories provided");
    return null;
  }

  const t0 = performance.now();
  onProgress?.("Collecting samples...");

  const frames = data.curve.computeFrenetFrames(FRAME_SAMPLES, false);

  // 1. Collect 3D samples with indicator values
  const samples: RBFSample[] = [];

  for (const salvo of salvoData.salvos) {
    const scanMwd = mwdRawData.get(salvo.scan);
    if (!scanMwd) continue;

    const t = Math.max(0, Math.min(1, salvo.faceStation / data.totalLength));
    const frameIdx = Math.round(t * FRAME_SAMPLES);
    const center = data.curve.getPointAt(t);
    const normal = frames.normals[frameIdx];
    const binormal = frames.binormals[frameIdx];
    const tangent = frames.tangents[frameIdx];

    for (let holeIdx = 0; holeIdx < salvo.holes.length; holeIdx++) {
      const hole = salvo.holes[holeIdx];
      const mwdHole = scanMwd.holes[holeIdx];
      if (!mwdHole) continue;
      const sensorVals = mwdHole.values[sensor];
      if (!sensorVals || sensorVals.length === 0) continue;
      // Skip if data type doesn't match mode
      if (isCategorical && typeof sensorVals[0] !== "string") continue;
      if (!isCategorical && typeof sensorVals[0] === "string") continue;

      const sx = center.x + hole.x * binormal.x - hole.y * normal.x;
      const sy = center.y + hole.x * binormal.y - hole.y * normal.y;
      const sz = center.z + hole.x * binormal.z - hole.y * normal.z;

      const dx = tangent.x + (hole.spreadX || 0) * binormal.x + (hole.spreadY || 0) * (-normal.x);
      const dy = tangent.y + (hole.spreadX || 0) * binormal.y + (hole.spreadY || 0) * (-normal.y);
      const dz = tangent.z + (hole.spreadX || 0) * binormal.z + (hole.spreadY || 0) * (-normal.z);

      const step = Math.max(1, Math.floor(mwdHole.depths.length / 12));
      for (let i = 0; i < mwdHole.depths.length; i += step) {
        const depth = mwdHole.depths[i];
        const rawVal = sensorVals[i];
        if (rawVal === undefined || rawVal === null) continue;
        if (!isCategorical && isNaN(rawVal as number)) continue;

        const px = sx + depth * dx;
        const py = sy + depth * dy;
        const pz = sz + depth * dz;

        const inside = isCategorical
          ? selectedCategories!.has(rawVal as string)
          : (rawVal as number) >= clampRange!.min && (rawVal as number) <= clampRange!.max;
        samples.push({ x: px, y: py, z: pz, value: inside ? 1.0 : 0.35 });
      }
    }
  }

  if (samples.length < 10) {
    console.warn("[RBF] Too few samples:", samples.length);
    return null;
  }

  const insidePoints = samples.filter(s => s.value > 0.5);
  console.log(`[RBF] ${samples.length} samples, ${insidePoints.length} inside filter range`);

  if (insidePoints.length === 0 || insidePoints.length === samples.length) {
    console.warn("[RBF] All samples are on one side — no boundary to extract");
    return null;
  }

  // 2. Use user-supplied plane normal or fit one via PCA
  onProgress?.("Fitting plane...");
  const planeNormal = userPlaneNormal ?? fitPlaneNormal(insidePoints);
  console.log(`[RBF] Plane normal: [${planeNormal.map(v => v.toFixed(3)).join(", ")}]${userPlaneNormal ? " (user-drawn)" : " (PCA)"}, flatness: ${flatness}`);

  // 3. Bounding box with padding
  onProgress?.("Computing grid...");
  let bx0 = Infinity, by0 = Infinity, bz0 = Infinity;
  let bx1 = -Infinity, by1 = -Infinity, bz1 = -Infinity;
  for (const s of samples) {
    if (s.x < bx0) bx0 = s.x; if (s.y < by0) by0 = s.y; if (s.z < bz0) bz0 = s.z;
    if (s.x > bx1) bx1 = s.x; if (s.y > by1) by1 = s.y; if (s.z > bz1) bz1 = s.z;
  }
  const pad = 1.0;
  const origin: [number, number, number] = [bx0 - pad, by0 - pad, bz0 - pad];
  const size: [number, number, number] = [
    (bx1 - bx0) + 2 * pad,
    (by1 - by0) + 2 * pad,
    (bz1 - bz0) + 2 * pad,
  ];

  // 4. Evaluate anisotropic Gaussian RBF on grid
  const grid = evaluateOnGrid(samples, origin, size, [resolution, resolution, resolution], power, radius, planeNormal, flatness);

  // 4b. Clamp boundary voxels to 0 so marching cubes closes the surface at grid edges
  const { nx, ny, nz } = grid;
  for (let iz = 0; iz < nz; iz++) {
    for (let iy = 0; iy < ny; iy++) {
      for (let ix = 0; ix < nx; ix++) {
        if (ix === 0 || ix === nx - 1 || iy === 0 || iy === ny - 1 || iz === 0 || iz === nz - 1) {
          grid.data[ix + iy * nx + iz * nx * ny] = 0;
        }
      }
    }
  }

  // 5. Extract isosurface
  onProgress?.("Extracting surface...");
  const verts = marchingCubes(grid, isoValue);

  if (verts.length === 0) {
    console.warn("[RBF] No isosurface generated");
    return null;
  }

  const geo = new THREE.BufferGeometry();
  geo.setAttribute("position", new THREE.BufferAttribute(verts, 3));
  geo.computeVertexNormals();

  const timeMs = performance.now() - t0;
  const triangleCount = verts.length / 9;
  console.log(`[RBF] Done: ${triangleCount} triangles in ${timeMs.toFixed(0)}ms`);

  return { geometry: geo, grid, triangleCount, sampleCount: samples.length, planeNormal, timeMs };
}
